<script setup lang="ts">
import bannerSvg from '#core/assets/img/icon/banner.svg';
import {
  AppBannerBackground,
  AppButton,
  AppPassword,
  AppPasswordCriteria,
  AppTextField,
  AppTypography,
} from '#core/components';
import type { FormErrors } from '#core/types/Form';
import type { RegisterForm } from '#modules/auth/types/Auth';
import { useDisplay } from 'vuetify/framework';

const { validations } = useValidation();
const { getSession } = useAuth();
const { setToken } = useAuthState();
const router = useRouter();
const display = useDisplay();

const tabletAndLessSize = computed(() => {
  return display.width.value <= 1366;
});

const formData = ref<RegisterForm>({
  email: '',
  password: '',
  password_confirmation: '',
});

const rules = computed(() => ({
  email: {
    required: validations.required('Email'),
    email: validations.email(),
  },
  password: {
    required: validations.required('Password'),
    minLength: validations.minLength('Password', 8),
    password: validations.password(),
  },
  password_confirmation: {
    required: validations.required('Confirm Password'),
    sameAs: validations.sameAs('Confirm Password', 'Password', formData.value.password),
  },
}));

const { form, handleSubmit, getErrors, v$ } = useForm<RegisterForm>({
  form: formData,
  rules: rules,
});

const hasSubmitted = ref(false);
const alreadyUsedEmail = ref('');

const { data, pending: registerLoading, execute: execSubmit, error } = useSubmit();

const registerError = ref<{ data: FormErrors<RegisterForm> }>({ data: { errors: {}, message: '' } });

const handleSignUp = async (): Promise<void> => {
  hasSubmitted.value = true;

  // Manually trigger validation
  v$.value.$touch();

  await handleSubmit(async () => {
    try {
      registerLoading.value = true;
      await execSubmit('auth/register', {
        method: 'POST',
        body: {
          email: form.value.email,
          password: form.value.password,
          password_confirmation: form.value.password_confirmation,
        },
      });

      if (error.value) {
        const api = error.value as { data: FormErrors<RegisterForm> };

        if (api.data.errors.email?.length) {
          api.data.errors.email = ['Email already taken, try signing in instead.'];
          alreadyUsedEmail.value = form.value.email;
        }

        registerError.value = api;
        return;
      }

      if (data.value) {
        setToken(data.value.data.access_token);
        await getSession();
        await router.push({ name: 'verify' });
      }
    } catch (error) {
      registerError.value = error as { data: FormErrors<RegisterForm> };
    } finally {
      registerLoading.value = false;
    }
  });
};

const errors = computed(() => {
  return getErrors(registerError.value?.data?.errors);
});

const validationErrors = computed(() => {
  // Only show errors after form has been submitted
  if (!hasSubmitted.value) {
    return { email: [], password: [], password_confirmation: [] };
  }

  const emailErrors = v$.value.email?.$errors.map((e: any) => e.$message) || [];
  const passwordErrors = v$.value.password?.$errors.map((e: any) => e.$message) || [];
  const passwordConfirmationErrors = v$.value.password_confirmation?.$errors.map((e: any) => e.$message) || [];

  return {
    email: emailErrors,
    password: passwordErrors,
    password_confirmation: passwordConfirmationErrors,
  };
});

const visibleErrors = computed(() => {
  const valErrors = validationErrors.value;
  const serverErrors = errors.value;

  return {
    email: [
      ...(Array.isArray(valErrors.email) ? valErrors.email : []),
      ...(Array.isArray(serverErrors.email) ? serverErrors.email : []),
    ],
    password: [
      ...(Array.isArray(valErrors.password) ? valErrors.password : []),
      ...(Array.isArray(serverErrors.password) ? serverErrors.password : []),
    ],
    password_confirmation: [
      ...(Array.isArray(valErrors.password_confirmation) ? valErrors.password_confirmation : []),
      ...(Array.isArray(serverErrors.password_confirmation) ? serverErrors.password_confirmation : []),
    ],
  };
});

const canContinue = computed(() => {
  return !v$.value.$invalid;
});

const showPasswordCriteria = computed(() => form.value.password.length > 0);

watch(
  () => form.value.email,
  () => {
    const errs = registerError.value.data.errors;
    if (errs.email?.includes('Email already taken, try signing in instead.')) {
      delete errs.email;
    }
  }
);
</script>

<template>
  <div class="register-static">
    <!-- Main Content -->
    <div class="main-content">
      <!-- Banner Section (Left) -->
      <div v-if="!tabletAndLessSize" class="banner-section">
        <AppBannerBackground :src="bannerSvg" />
      </div>

      <!-- Form Section (Right) -->
      <div class="form-section">
        <div class="form-container">
          <!-- Header -->
          <div class="form-header">
            <AppTypography element="h1" type="title-4" class="form-title">Welcome!</AppTypography>
            <AppTypography element="p" type="body-1" class="form-subtitle"
              >Enter your email and password to create your account.</AppTypography
            >
          </div>

          <!-- Form Fields -->
          <form class="form-fields" @submit.prevent="handleSignUp">
            <!-- Email Field -->
            <AppTextField
              v-model="form.email"
              type="email"
              label="Email"
              placeholder="example@address.com.au"
              show-label
              :error-messages="visibleErrors.email"
              :disabled="registerLoading"
            />

            <!-- Password Field -->
            <AppPassword v-model="form.password" placeholder="Password" :disabled="registerLoading" />

            <!-- Password Criteria -->
            <div v-if="showPasswordCriteria" class="password-criteria-section px-3 pb-3">
              <AppPasswordCriteria :password="form.password" />
            </div>

            <!-- Confirm Password Field -->
            <AppPassword
              v-model="form.password_confirmation"
              label="Confirm Password"
              placeholder="Confirm Password"
              :disabled="registerLoading"
            />

            <div class="d-flex flex-column ga-4 text-center mt-3">
              <!-- Continue Button -->
              <AppButton
                type="submit"
                block
                class="btn-continue"
                :loading="registerLoading"
                :disabled="!canContinue || registerLoading"
                data-cy="submit"
                data-testid="submit"
                @click="handleSignUp"
              >
                Continue
              </AppButton>

              <!-- Login Link -->
              <AppTypography element="p" type="body-1" class="login-link">
                Already have an account?
                <nuxt-link to="/login" class="login-link-text" data-cy="log-in"> Login </nuxt-link>
              </AppTypography>

              <!-- Terms -->
              <AppTypography element="p" type="caption-1" class="terms-text">
                By continuing, you agree to our
                <NuxtLink href="/terms" class="terms-link">Terms of Service</NuxtLink>
                and
                <NuxtLink href="/privacy" class="terms-link">Privacy Policy.</NuxtLink>
              </AppTypography>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '#core/assets/scss/partials/fonts' as *;

.register-static {
  background-color: #f7f7f8;
  font-family: 'Noto Sans', sans-serif;
}

// Main Content
.main-content {
  display: flex;
  height: calc(100vh - 72px);
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

// Banner Section
.banner-section {
  flex: 1 0 25%;
  width: 45%;
  padding: 24px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.banner-logo {
  display: flex;
  align-items: center;
  gap: 8px;

  .banner-logo-icon {
    width: 24px;
    height: 24px;
  }

  .banner-logo-text {
    font-family: $font-family-display !important;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
    color: #ffffff;
    letter-spacing: 0.4px;
  }
}

.banner-tagline {
  font-family: $font-family-display !important;
  font-size: 80px;
  font-weight: 700;
  line-height: 80px;
  color: #ffffff;
  margin: 0;
}

// Form Section
.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px 40px 40px;
  background-color: #f7f7f8;
}

.form-container {
  width: 100%;
  max-width: 480px;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-family: $font-family-display !important;
  font-size: 28px;
  font-weight: 700;
  line-height: 32px;
  color: #01011d;
  margin: 0 0 4px 0;
}

.form-subtitle {
  font-size: 16px;
  line-height: 20px;
  color: #01011d;
  margin: 0;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 0;

  > * {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  :deep(.v-input__details) {
    height: 16px !important;
    min-height: 16px !important;
    max-height: 16px !important;
  }
}

.password-criteria-section {
  margin-top: 0;
  background-color: transparent;

  :deep(.font-footnote) {
    font-size: 14px;
    line-height: 18px;
    color: #414156;
  }

  :deep(.password-checked .font-footnote) {
    color: #05704e;
    font-weight: 600;
  }
}

.btn-continue {
  height: 48px !important;
  border-radius: 64px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  margin-top: 8px;
  background-color: #ffdf27 !important;
  color: #01011d !important;
  touch-action: manipulation !important;
  cursor: pointer !important;
  -webkit-tap-highlight-color: transparent;
}

.login-link {
  text-align: center;
  font-size: 16px;
  line-height: 20px;
  color: #01011d;
  margin: 8px 0;

  .login-link-text {
    font-weight: 700;
    color: #01011d;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.terms-text {
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  color: #414156;

  .terms-link {
    font-weight: 600;
    color: #01011d;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 1440px) {
  .banner-section {
    flex: 0 0 45%;
    width: 45%;
  }
}

@media (max-width: 960px) {
  .main-content {
    flex-direction: column;
  }

  .banner-section {
    display: none;
  }

  .form-section {
    padding: 24px 16px;
    background-color: #ffffff;
  }
}
</style>
